"use client";

import React from "react";
import { useLocation } from "react-router-dom";
import { useFunnelNavigate } from "@/hooks/useFunnelNavigate";
import { useTranslation } from "react-i18next";
import { getOffer } from "@/api/simpego";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { OfferResponse } from "@/types/simpego-api";

interface UseOfferLoaderProps {
  setFunnelStateFromOffer: (offer: OfferResponse) => string; // Now returns the target path key
  handleResetFunnel: () => void;
  setIsOfferLoadingFromUrl: (isLoading: boolean) => void;
  currentLocale: string;
}


export const useOfferLoader = ({
  setFunnelStateFromOffer,
  handleResetFunnel,
  setIsOfferLoadingFromUrl,
  currentLocale,
}: UseOfferLoaderProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useFunnelNavigate();
  const location = useLocation();
  const { showErrorDialog } = useErrorDialog();
  const [isInitialLoad, setIsInitialLoad] = React.useState<boolean>(true);

  React.useEffect(() => {
    // Parse query parameters from the hash fragment
    const hashQuery = location.search; // For HashRouter, location.search contains the query string from the hash
    const searchParams = new URLSearchParams(hashQuery);
    const urlContractId = searchParams.get("contractID");
    const urlContractToken = searchParams.get("contractToken");

    if (urlContractId && urlContractToken && isInitialLoad) {
      setIsInitialLoad(false);
      const loadOffer = async () => {
        setIsOfferLoadingFromUrl(true);
        try {
          console.log("Attempting to load offer with:", { urlContractToken, urlContractId });

          const offerResponse = await getOffer(urlContractToken, urlContractId);

          // Redirect externally when the offer has already been applied
          const isApplied = offerResponse.status?.toLowerCase() === "applied";
          const appliedRedirectUrl = i18n.getResource(currentLocale, "translation", "externalLinks.appliedOffer") as string | undefined;
          if (isApplied && appliedRedirectUrl) {
            window.location.href = appliedRedirectUrl;
            return;
          }

          // IMPORTANT: Ensure i18n language is set to the URL's locale before processing the offer
          if (i18n.language !== currentLocale) {
            console.log(`useOfferLoader: Changing i18n language from ${i18n.language} to ${currentLocale}`);
            await i18n.changeLanguage(currentLocale);
          }

          const routeKey = setFunnelStateFromOffer(offerResponse); // Get route key like "routes.confirmation"
          
          // Prepend the current locale to the target path
          // Use i18n.getResource to explicitly get the translation for the currentLocale
          const baseRoute = i18n.getResource(currentLocale, 'translation', routeKey) as string;
          const localizedTargetPath = `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
          navigate(localizedTargetPath, { replace: true, state: { fromOfferLoad: true } }); // Added fromOfferLoad flag

        } catch (error) {
          console.error("Failed to load offer from URL:", error);
          showErrorDialog(t("common.errorLoadingOffer"));
          handleResetFunnel();
          navigate(`/${currentLocale}${i18n.getResource(currentLocale, 'translation', 'routes.home')}`, { replace: true }); // Navigate to localized home on error
        } finally {
          setIsOfferLoadingFromUrl(false); // Set immediately
        }
      };
      loadOffer();
    }
  }, [location.search, isInitialLoad, setFunnelStateFromOffer, t, handleResetFunnel, navigate, setIsOfferLoadingFromUrl, currentLocale, i18n, showErrorDialog]);
};
